'use client';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDebounce } from 'use-debounce';
import { useModelTraining } from '.';
import { useEffect } from 'react';
import { useModelByName } from '@/lib/hooks/queries/useModels';
import { Loader2 } from 'lucide-react';

export default function ModelNameInput() {
  const { modelName, setModelName, modelNameError, setModelNameError } =
    useModelTraining();
  const [debouncedName] = useDebounce(modelName, 300);

  const validateModelName = (name: string): boolean => {
    if (name.length === 0) return true;
    const regex = /^[a-z0-9][a-z0-9._-]*[a-z0-9]$|^[a-z0-9]$/;
    return regex.test(name);
  };

  const { data: existingModel, isLoading: checkingName } =
    useModelByName(debouncedName);

  useEffect(() => {
    if (debouncedName.length === 0) {
      setModelNameError(null);
      return;
    }

    if (!validateModelName(debouncedName)) {
      setModelNameError(
        'Enter a valid name. This value may contain only lowercase letters, numbers, dashes, underscores, or periods, and may not start or end with a dash, underscore, or period',
      );
    } else if (existingModel) {
      setModelNameError('A model with this name already exists.');
    } else {
      setModelNameError(null);
    }
  }, [debouncedName, existingModel]);

  const handleNameChange = (value: string) => {
    setModelName(value);
  };

  return (
    <div className='px-4 space-y-2'>
      <Label
        htmlFor='model-name'
        className='text-base font-semibold text-neutral-50 leading-tight'
      >
        Name
      </Label>
      <div className='relative'>
        {checkingName && (
          <div className='absolute inset-y-0 right-0 flex items-center pr-3'>
            <Loader2 className='h-4 w-4 text-fuchsia-400 animate-spin' />
          </div>
        )}
        <Input
          id='model-name'
          placeholder='Enter a name of your style'
          value={modelName}
          onChange={(e) => handleNameChange(e.target.value)}
          className={`bg-neutral-800 border text-stone-50/90 text-sm placeholder:text-stone-50/90/40 focus:ring-2 focus:ring-fuchsia-600/50 transition ${
            modelNameError
              ? 'border-red-500 focus:border-red-500'
              : 'border-white/10 focus:border-fuchsia-600'
          } ${checkingName ? 'pr-10' : ''}`}
        />
      </div>
      {modelNameError ? (
        <Label className='text-xs text-red-400'>{modelNameError}</Label>
      ) : (
        <Label className='text-xs text-stone-50/60'>
          Short name used to identify your model in Image Generator.
        </Label>
      )}
    </div>
  );
}
